import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Timestamp } from '@applied/proto/google/protobuf/timestamp_pb';
import type { MarketConditions } from '@applied/proto/web_services/candidate_experience/proto/common_pb';
import type {
  Account,
  Candidate,
  Employee,
} from '@applied/proto/web_services/candidate_experience/proto/user_pb';
import type {
  WebOfferCompensationInfo as ProtoWebOfferCompensationInfo,
  WebOfferJobInfo as ProtoWebOfferJobInfo,
  WebOfferSelection as ProtoWebOfferSelection,
} from '@applied/proto/web_services/candidate_experience/proto/web_offer_pb';
import { useMonotonicState } from 'api/typing';

import type { BenefitsType } from 'components/WebOfferPage/benefits_classes';

export type EquityCalculationVariables = {
  selectedSalarySubdivisions?: number;
  selectedSignOnBonusSubdivisions?: number;
};

export type EquityCalculationConstants = {
  baseSalarySubdivisions: number;
  salaryEquityRatio: number;
  maxSignOnBonusSubdivisions: number;
  signOnBonusEquityRatio: number;
  baseEquityUsdCents: number;
  usdCentsToOfferSubdivisions: number;
};

export function calculateEquity(
  variables: EquityCalculationVariables,
  constants: EquityCalculationConstants,
): number {
  const { selectedSalarySubdivisions, selectedSignOnBonusSubdivisions } = variables;
  const {
    baseSalarySubdivisions,
    salaryEquityRatio,
    maxSignOnBonusSubdivisions,
    signOnBonusEquityRatio,
    baseEquityUsdCents,
    usdCentsToOfferSubdivisions,
  } = constants;

  if (
    selectedSalarySubdivisions === undefined ||
    selectedSignOnBonusSubdivisions === undefined ||
    usdCentsToOfferSubdivisions === 0
  ) {
    return baseEquityUsdCents;
  }

  // How much above the base salary has the candidate selected when converted to USD cents.
  const salaryDeltaUsdCents =
    (selectedSalarySubdivisions - baseSalarySubdivisions) / usdCentsToOfferSubdivisions;
  // The amount of equity the candidate is sacrificing for the increased salary.
  const salaryEquityTradeoffUsdCents = salaryDeltaUsdCents * salaryEquityRatio;

  // How much above the base sign-on bonus has the candidate selected when converted to USD cents.
  const signOnBonusDeltaUsdCents =
    (selectedSignOnBonusSubdivisions - maxSignOnBonusSubdivisions) / usdCentsToOfferSubdivisions;
  // The amount of equity the candidate is sacrificing for the increased sign-on bonus.
  const signOnBonusEquityTradeoffUsdCents = signOnBonusDeltaUsdCents * signOnBonusEquityRatio;

  return baseEquityUsdCents - salaryEquityTradeoffUsdCents - signOnBonusEquityTradeoffUsdCents;
}

export type WebOfferJobInfo = {
  jobTitle: string | undefined;
  atsJobPostUrl: string | null | undefined;
  teamName: string | undefined;
  teamPageUrl: string | null | undefined;
  setJobTitle: (updatedJobTitle: string) => void;
  setAtsJobPostUrl: (updatedGreenhouseJobPostUrl: string) => void;
  setTeamName: (updatedTeamName: string) => void;
  setTeamPageUrl: (updatedTeamPageUrl: string) => void;
};

export type WebOfferCompensationInfo = {
  currency: string | undefined;
  benefitsType: string | undefined;
  baseSalarySubdivisions: number | undefined;
  salaryThresholdPercentage: number | undefined;
  maxSignOnBonusSubdivisions: number | undefined;
  baseEquityUsdCents: number | undefined;
  salaryEquityRatio: number | undefined;
  signOnBonusEquityRatio: number | undefined;
  relocationBonusSubdivisions: number | undefined;
  annualBonusType: string | undefined;
  annualBonusValue: number | undefined;
  isSalaryStatic: boolean;
  isSignOnBonusStatic: boolean;
  setCurrency: (updatedCurrency: string) => void;
  setBenefitsType: (updatedBenefitsType: string) => void;
  setBaseSalarySubdivisions: (updatedBaseSalarySubdivisions: number) => void;
  setSalaryThresholdPercentage: (updatedSalaryThresholdPercentage: number) => void;
  setMaxSignOnBonusSubdivisions: (updatedMaxSignOnBonusSubdivisions: number) => void;
  setBaseEquityUsdCents: (updatedBaseEquityUsdCents: number) => void;
  setSalaryEquityRatio: (updatedSalaryEquityRatio: number) => void;
  setSignOnBonusEquityRatio: (updatedSignOnBonusEquityRatio: number) => void;
  setRelocationBonusSubdivisions: (updatedRelocationBonusSubdivisions: number) => void;
  setAnnualBonusType: (updatedAnnualBonusType: string) => void;
  setAnnualBonusValue: (updatedAnnualBonusValue: number) => void;
  setIsSalaryStatic: (updatedIsSalaryStatic: boolean) => void;
  setIsSignOnBonusStatic: (updatedIsSignOnBonusStatic: boolean) => void;
};

export type WebOfferSelection = {
  isFinal: boolean;
  salarySubdivisions: number | undefined;
  signOnBonusSubdivisions: number | undefined;
  equityUsdCents: number | undefined;
  setIsFinal: (updatedIsFinal: boolean) => void;
  setSalarySubdivisions: (updatedSalarySubdivisions: number) => void;
  setSignOnBonusSubdivisions: (updatedSignOnBonusSubdivisions: number) => void;
  setEquityUsdCents: (updatedEquityUsdCents: number) => void;
};

type SyncJobInfoParams = {
  protoJobInfo: ProtoWebOfferJobInfo;
  setJobTitle: (jobTitle: string) => void;
  setAtsJobPostUrl: (atsJobPostUrl: string | null) => void;
  setTeamName: (teamName: string) => void;
  setTeamPageUrl: (teamPageUrl: string | null) => void;
};

export function syncJobInfo({
  protoJobInfo,
  setJobTitle,
  setAtsJobPostUrl,
  setTeamName,
  setTeamPageUrl,
}: SyncJobInfoParams): void {
  setJobTitle(protoJobInfo.jobTitle);
  setAtsJobPostUrl(protoJobInfo.atsJobPostUrl ?? null);
  setTeamName(protoJobInfo.teamName);
  setTeamPageUrl(protoJobInfo.teamPageUrl ?? null);
}

type SyncCompensationInfoParams = {
  protoCompensationInfo: ProtoWebOfferCompensationInfo;
  setCurrency: (currency: string) => void;
  setCompensationBenefitsType: (benefitsType: string) => void;
  setBaseSalarySubdivisions: (baseSalarySubdivisions: number) => void;
  setSalaryThresholdPercentage: (salaryThresholdPercentage: number) => void;
  setMaxSignOnBonusSubdivisions: (maxSignOnBonusSubdivisions: number) => void;
  setBaseEquityUsdCents: (baseEquityUsdCents: number) => void;
  setSalaryEquityRatio: (salaryEquityRatio: number) => void;
  setSignOnBonusEquityRatio: (signOnBonusEquityRatio: number) => void;
  setRelocationBonusSubdivisions: (relocationBonusSubdivisions: number) => void;
  setAnnualBonusType: (annualBonusType: string) => void;
  setAnnualBonusValue: (annualBonusValue: number) => void;
  setIsSalaryStatic: (isSalaryStatic: boolean) => void;
  setIsSignOnBonusStatic: (isSignOnBonusStatic: boolean) => void;
};

export function syncCompensationInfo({
  protoCompensationInfo,
  setCurrency,
  setCompensationBenefitsType,
  setBaseSalarySubdivisions,
  setSalaryThresholdPercentage,
  setMaxSignOnBonusSubdivisions,
  setBaseEquityUsdCents,
  setSalaryEquityRatio,
  setSignOnBonusEquityRatio,
  setRelocationBonusSubdivisions,
  setAnnualBonusType,
  setAnnualBonusValue,
  setIsSalaryStatic,
  setIsSignOnBonusStatic,
}: SyncCompensationInfoParams): void {
  setCurrency(protoCompensationInfo.currency);
  setCompensationBenefitsType(protoCompensationInfo.benefitsType);
  setBaseSalarySubdivisions(Number(protoCompensationInfo.baseSalarySubdivisions));
  setSalaryThresholdPercentage(protoCompensationInfo.salaryThresholdPercentage);
  setMaxSignOnBonusSubdivisions(Number(protoCompensationInfo.maxSignOnBonusSubdivisions));
  setBaseEquityUsdCents(Number(protoCompensationInfo.baseEquityUsdCents));
  setSalaryEquityRatio(protoCompensationInfo.salaryEquityRatio);
  setSignOnBonusEquityRatio(protoCompensationInfo.signOnBonusEquityRatio);
  setRelocationBonusSubdivisions(Number(protoCompensationInfo.relocationBonusSubdivisions));
  setAnnualBonusType(protoCompensationInfo.annualBonusType);
  setAnnualBonusValue(Number(protoCompensationInfo.annualBonusValue));
  setIsSalaryStatic(protoCompensationInfo.isSalaryStatic);
  setIsSignOnBonusStatic(protoCompensationInfo.isSignOnBonusStatic);
}

type SyncSelectionParams = {
  protoSelection: ProtoWebOfferSelection;
  setSalarySubdivisions: (salarySubdivisions: number) => void;
  setSignOnBonusSubdivisions: (signOnBonusSubdivisions: number) => void;
  setIsFinal: (isFinal: boolean) => void;
  setSelectionSalarySubdivisions: (salarySubdivisions: number) => void;
  setSelectionSignOnBonusSubdivisions: (signOnBonusSubdivisions: number) => void;
  setSelectionEquityUsdCents: (equityUsdCents: number) => void;
};

export function syncSelection({
  protoSelection,
  setSalarySubdivisions,
  setSignOnBonusSubdivisions,
  setIsFinal,
  setSelectionSalarySubdivisions,
  setSelectionSignOnBonusSubdivisions,
  setSelectionEquityUsdCents,
}: SyncSelectionParams): void {
  setSalarySubdivisions(Number(protoSelection.salarySubdivisions));
  setSignOnBonusSubdivisions(Number(protoSelection.signOnBonusSubdivisions));
  setIsFinal(protoSelection.isFinal);
  setSelectionSalarySubdivisions(Number(protoSelection.salarySubdivisions));
  setSelectionSignOnBonusSubdivisions(Number(protoSelection.signOnBonusSubdivisions));
  setSelectionEquityUsdCents(Number(protoSelection.equityUsdCents));
}

type SyncSelectionsFromCompensationParams = {
  protoCompensationInfo: ProtoWebOfferCompensationInfo;
  setSalarySubdivisions: (salarySubdivisions: number) => void;
  setSignOnBonusSubdivisions: (signOnBonusSubdivisions: number) => void;
};

export function syncSelectionsFromCompensation({
  protoCompensationInfo,
  setSalarySubdivisions,
  setSignOnBonusSubdivisions,
}: SyncSelectionsFromCompensationParams): void {
  setSalarySubdivisions(Number(protoCompensationInfo.baseSalarySubdivisions));
  setSignOnBonusSubdivisions(Number(protoCompensationInfo.maxSignOnBonusSubdivisions));
}

export function getEquityCalculationConstants(
  compensationInfo?: WebOfferCompensationInfo,
  marketConditions?: MarketConditions,
): EquityCalculationConstants {
  const defaultConstants: EquityCalculationConstants = {
    baseSalarySubdivisions: 0,
    salaryEquityRatio: 0,
    maxSignOnBonusSubdivisions: 0,
    signOnBonusEquityRatio: 0,
    baseEquityUsdCents: 0,
    usdCentsToOfferSubdivisions: 1,
  };
  if (!compensationInfo || !marketConditions) {
    return defaultConstants;
  }
  const {
    baseSalarySubdivisions,
    maxSignOnBonusSubdivisions,
    baseEquityUsdCents,
    salaryEquityRatio,
    signOnBonusEquityRatio,
  } = compensationInfo;
  const { usdCentsToOfferSubdivisions } = marketConditions;

  if (
    baseSalarySubdivisions === undefined ||
    maxSignOnBonusSubdivisions === undefined ||
    baseEquityUsdCents === undefined ||
    salaryEquityRatio === undefined ||
    signOnBonusEquityRatio === undefined ||
    usdCentsToOfferSubdivisions === undefined
  ) {
    return defaultConstants;
  }

  return {
    baseSalarySubdivisions,
    salaryEquityRatio,
    maxSignOnBonusSubdivisions,
    signOnBonusEquityRatio,
    baseEquityUsdCents,
    usdCentsToOfferSubdivisions,
  };
}

function destructureAccount(account: Account | undefined): AccountFields {
  if (!account) {
    return {
      resourceUuid: null,
      email: null,
      firstName: null,
      lastName: null,
      profileImageUrl: null,
    };
  }
  return {
    resourceUuid: account.resourceUuid ?? null,
    email: account.email ?? null,
    firstName: account.firstName ?? null,
    lastName: account.lastName ?? null,
    profileImageUrl: account.profileImageUrl ?? null,
  };
}

function destructureCandidate(candidate: Candidate | undefined): CandidateFields {
  if (!candidate) {
    return {
      resourceUuid: null,
      applicationId: null,
      accountResourceUuid: null,
      email: null,
      firstName: null,
      lastName: null,
      profileImageUrl: null,
    };
  }
  const { resourceUuid: accountResourceUuid, ...accountFields } = destructureAccount(
    candidate.accountInfo,
  );
  return {
    resourceUuid: candidate.resourceUuid ?? null,
    applicationId: candidate.applicationId ?? null,
    accountResourceUuid,
    ...accountFields,
  };
}

function destructureEmployee(employee: Employee | undefined): EmployeeFields {
  if (!employee) {
    return {
      resourceUuid: null,
      anaheimUuid: null,
      linkedinUrl: null,
      accountResourceUuid: null,
      email: null,
      firstName: null,
      lastName: null,
      profileImageUrl: null,
    };
  }
  const { resourceUuid: accountResourceUuid, ...accountFields } = destructureAccount(
    employee.accountInfo,
  );
  return {
    resourceUuid: employee.resourceUuid ?? null,
    anaheimUuid: employee.anaheimUuid ?? null,
    linkedinUrl: employee.linkedinUrl ?? null,
    accountResourceUuid,
    ...accountFields,
  };
}

// Values are set to undefined when data has not been loaded yet.
// Values should never be set to undefined. If they truly do not exist on the backend, they should
// be set to null.
// If a value should not be non-existent, it should not be able to be set to null, and the page should
// panic.

export type AccountFields = {
  resourceUuid: string | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  profileImageUrl: string | null | undefined;
};

export type CandidateFields = {
  resourceUuid: string | null | undefined;
  applicationId: string | null | undefined;
  accountResourceUuid: string | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  profileImageUrl: string | null | undefined;
};

export type EmployeeFields = {
  resourceUuid: string | null | undefined;
  anaheimUuid: string | null | undefined;
  linkedinUrl: string | null | undefined;
  accountResourceUuid: string | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  profileImageUrl: string | null | undefined;
};

export type WebOfferInfo = {
  candidate: CandidateFields;
  setCandidate: (updatedCandidate: Candidate) => void;
  manager: EmployeeFields;
  setManager: (updatedManager: Employee) => void;
  teamMembers: EmployeeFields[];
  setTeamMembers: (updatedTeamMembers: Employee[]) => void;
  jobInfo: WebOfferJobInfo;
  selections: {
    salarySubdivisions: number | undefined;
    signOnBonusSubdivisions: number | undefined;
  };
  setSalarySubdivisions: (salarySubdivisions: number) => void;
  setSignOnBonusSubdivisions: (signOnBonusSubdivisions: number) => void;
  compensationInfo: WebOfferCompensationInfo;
  marketConditions: MarketConditions | undefined;
  setMarketConditions: (updatedMarketConditions: MarketConditions) => void;
  deadline: Timestamp | undefined;
  setDeadline: (updatedDeadline: Timestamp) => void;
  selection: WebOfferSelection;
  benefitsType: BenefitsType | null;
  setBenefitsType: (benefitsType: BenefitsType | null) => void;
  equityCalculationConstants: EquityCalculationConstants;
  equityUsdCents: number;
  isRequestingOfferLetter: boolean;
  setIsRequestingOfferLetter: (isRequesting: boolean) => void;
  show50HourWorkWeekVideo: boolean | undefined;
  setShow50HourWorkWeekVideo: (show50HourWorkWeekVideo: boolean) => void;
};

export const WebOfferContext = createContext<WebOfferInfo | null>(null);

export const WebOfferContextProvider = (props: { children?: JSX.Element }): JSX.Element => {
  const { children } = props;

  const [candidateProto, setCandidateProto] = useMonotonicState<Candidate>();
  const [managerProto, setManagerProto] = useMonotonicState<Employee>();
  const [teamMembersProto, setTeamMembersProto] = useMonotonicState<Employee[]>();

  const setCandidate = useCallback(
    (updatedCandidate: Candidate): void => {
      setCandidateProto(updatedCandidate);
    },
    [setCandidateProto],
  );

  const setManager = useCallback(
    (updatedManager: Employee): void => {
      setManagerProto(updatedManager);
    },
    [setManagerProto],
  );

  const setTeamMembers = useCallback(
    (updatedTeamMembers: Employee[]): void => {
      setTeamMembersProto(updatedTeamMembers);
    },
    [setTeamMembersProto],
  );

  const candidate = useMemo(() => destructureCandidate(candidateProto), [candidateProto]);
  const manager = useMemo(() => destructureEmployee(managerProto), [managerProto]);
  const teamMembers = useMemo(
    () => (teamMembersProto ?? []).map((employee) => destructureEmployee(employee)),
    [teamMembersProto],
  );
  const [jobTitle, setJobTitle] = useMonotonicState<string>();
  const [atsJobPostUrl, setAtsJobPostUrl] = useMonotonicState<string | null>();
  const [teamName, setTeamName] = useMonotonicState<string>();
  const [teamPageUrl, setTeamPageUrl] = useMonotonicState<string | null>();
  const [salarySubdivisions, setSalarySubdivisions] = useMonotonicState<number>();
  const [signOnBonusSubdivisions, setSignOnBonusSubdivisions] = useMonotonicState<number>();
  const [currency, setCurrency] = useMonotonicState<string>();
  const [compensationBenefitsType, setCompensationBenefitsType] = useMonotonicState<string>();
  const [baseSalarySubdivisions, setBaseSalarySubdivisions] = useMonotonicState<number>();
  const [salaryThresholdPercentage, setSalaryThresholdPercentage] = useMonotonicState<number>();
  const [maxSignOnBonusSubdivisions, setMaxSignOnBonusSubdivisions] = useMonotonicState<number>();
  const [baseEquityUsdCents, setBaseEquityUsdCents] = useMonotonicState<number>();
  const [salaryEquityRatio, setSalaryEquityRatio] = useMonotonicState<number>();
  const [signOnBonusEquityRatio, setSignOnBonusEquityRatio] = useMonotonicState<number>();
  const [relocationBonusSubdivisions, setRelocationBonusSubdivisions] = useMonotonicState<number>();
  const [annualBonusType, setAnnualBonusType] = useMonotonicState<string>();
  const [annualBonusValue, setAnnualBonusValue] = useMonotonicState<number>();
  const [isSalaryStatic, setIsSalaryStatic] = useState<boolean>(false);
  const [isSignOnBonusStatic, setIsSignOnBonusStatic] = useState<boolean>(false);
  const [marketConditions, setMarketConditions] = useMonotonicState<MarketConditions>();
  const [deadline, setDeadline] = useMonotonicState<Timestamp>();
  const [isFinal, setIsFinal] = useState<boolean>(false);
  const [selectionSalarySubdivisions, setSelectionSalarySubdivisions] = useMonotonicState<number>();
  const [selectionSignOnBonusSubdivisions, setSelectionSignOnBonusSubdivisions] =
    useMonotonicState<number>();
  const [selectionEquityUsdCents, setSelectionEquityUsdCents] = useMonotonicState<number>();
  const [benefitsType, setBenefitsType] = useMonotonicState<BenefitsType | null>();
  const [isRequestingOfferLetter, setIsRequestingOfferLetter] = useState<boolean>(false);
  const [show50HourWorkWeekVideo, setShow50HourWorkWeekVideo] = useMonotonicState<boolean>();

  const jobInfo = useMemo(
    () => ({
      jobTitle,
      atsJobPostUrl,
      teamName,
      teamPageUrl,
      setJobTitle,
      setAtsJobPostUrl,
      setTeamName,
      setTeamPageUrl,
    }),
    [
      jobTitle,
      atsJobPostUrl,
      teamName,
      teamPageUrl,
      setJobTitle,
      setAtsJobPostUrl,
      setTeamName,
      setTeamPageUrl,
    ],
  );

  const compensationInfo = useMemo(
    () => ({
      currency,
      benefitsType: compensationBenefitsType,
      baseSalarySubdivisions,
      salaryThresholdPercentage,
      maxSignOnBonusSubdivisions,
      baseEquityUsdCents,
      salaryEquityRatio,
      signOnBonusEquityRatio,
      relocationBonusSubdivisions,
      annualBonusType,
      annualBonusValue,
      isSalaryStatic,
      isSignOnBonusStatic,
      setCurrency,
      setBenefitsType: setCompensationBenefitsType,
      setBaseSalarySubdivisions,
      setSalaryThresholdPercentage,
      setMaxSignOnBonusSubdivisions,
      setBaseEquityUsdCents,
      setSalaryEquityRatio,
      setSignOnBonusEquityRatio,
      setRelocationBonusSubdivisions,
      setAnnualBonusType,
      setAnnualBonusValue,
      setIsSalaryStatic,
      setIsSignOnBonusStatic,
    }),
    [
      currency,
      compensationBenefitsType,
      baseSalarySubdivisions,
      salaryThresholdPercentage,
      maxSignOnBonusSubdivisions,
      baseEquityUsdCents,
      salaryEquityRatio,
      signOnBonusEquityRatio,
      relocationBonusSubdivisions,
      annualBonusType,
      annualBonusValue,
      isSalaryStatic,
      isSignOnBonusStatic,
      setCurrency,
      setCompensationBenefitsType,
      setBaseSalarySubdivisions,
      setSalaryThresholdPercentage,
      setMaxSignOnBonusSubdivisions,
      setBaseEquityUsdCents,
      setSalaryEquityRatio,
      setSignOnBonusEquityRatio,
      setRelocationBonusSubdivisions,
      setAnnualBonusType,
      setAnnualBonusValue,
    ],
  );

  const selection = useMemo(
    () => ({
      isFinal,
      salarySubdivisions: selectionSalarySubdivisions,
      signOnBonusSubdivisions: selectionSignOnBonusSubdivisions,
      equityUsdCents: selectionEquityUsdCents,
      setIsFinal,
      setSalarySubdivisions: setSelectionSalarySubdivisions,
      setSignOnBonusSubdivisions: setSelectionSignOnBonusSubdivisions,
      setEquityUsdCents: setSelectionEquityUsdCents,
    }),
    [
      isFinal,
      selectionSalarySubdivisions,
      selectionSignOnBonusSubdivisions,
      selectionEquityUsdCents,
      setIsFinal,
      setSelectionSalarySubdivisions,
      setSelectionSignOnBonusSubdivisions,
      setSelectionEquityUsdCents,
    ],
  );

  const selections = useMemo(
    () => ({
      salarySubdivisions: salarySubdivisions,
      signOnBonusSubdivisions: signOnBonusSubdivisions,
    }),
    [salarySubdivisions, signOnBonusSubdivisions],
  );

  const equityCalculationConstants = useMemo(() => {
    return getEquityCalculationConstants(compensationInfo, marketConditions);
  }, [compensationInfo, marketConditions]);

  const calculatedEquityUsdCents = useMemo(() => {
    return calculateEquity(
      {
        selectedSalarySubdivisions: salarySubdivisions,
        selectedSignOnBonusSubdivisions: signOnBonusSubdivisions,
      },
      equityCalculationConstants,
    );
  }, [salarySubdivisions, signOnBonusSubdivisions, equityCalculationConstants]);

  const webOfferInfo: WebOfferInfo = useMemo(
    () => ({
      candidate,
      setCandidate,
      manager,
      setManager,
      teamMembers,
      setTeamMembers,
      jobInfo,
      selections,
      setSalarySubdivisions,
      setSignOnBonusSubdivisions,
      compensationInfo,
      marketConditions,
      setMarketConditions,
      deadline,
      setDeadline,
      selection,
      benefitsType: benefitsType ?? null,
      setBenefitsType,
      equityCalculationConstants,
      equityUsdCents: calculatedEquityUsdCents,
      isRequestingOfferLetter,
      setIsRequestingOfferLetter,
      show50HourWorkWeekVideo,
      setShow50HourWorkWeekVideo,
    }),
    [
      candidate,
      setCandidate,
      manager,
      setManager,
      teamMembers,
      setTeamMembers,
      jobInfo,
      selections,
      setSalarySubdivisions,
      setSignOnBonusSubdivisions,
      compensationInfo,
      marketConditions,
      setMarketConditions,
      deadline,
      setDeadline,
      selection,
      benefitsType,
      setBenefitsType,
      equityCalculationConstants,
      calculatedEquityUsdCents,
      isRequestingOfferLetter,
      setIsRequestingOfferLetter,
      show50HourWorkWeekVideo,
      setShow50HourWorkWeekVideo,
    ],
  );

  return <WebOfferContext.Provider value={webOfferInfo}>{children}</WebOfferContext.Provider>;
};

export const useWebOffer = (): WebOfferInfo => {
  const context = useContext(WebOfferContext);
  if (!context) {
    throw new Error('useWebOffer must be used within a WebOfferContextProvider');
  }
  return context;
};
